import useBookParams from '@monorepo/shared/src/hooks/use-book-params';
import CREATE_READING_LOG from 'graph/queries/books/create-reading-log';
import NewSticker from 'components/modules/Rewards/NewSticker';
import useBookStore from '@monorepo/shared/src/graph/state/query-hooks/use-book-store';
import { useCallback, useEffect, useState } from 'react';
import { useReadAloudPaging } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/ReadAloud';
import { useRouteGuard } from 'state/hooks/use-route-guard';
import { useMutation } from '@apollo/client';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import type { ReadingLog } from '@monorepo/shared/src/graph/schema-generated-types-student';

const useReadingLog = () => {
  const bookParams = useBookParams();
  const [{ book }] = useBookStore(bookParams);
  const [{ activePages }] = useReadAloudPaging();
  const { blockRouter, closeModal, handleLeaveWithoutSaving, nextLocation } = useRouteGuard();
  const [, { openModal }] = useModal();
  const [createReadingLog] = useMutation(CREATE_READING_LOG);

  const [pagesRead, setPagesRead] = useState<{ [page: number]: boolean }>({});
  const [dateStarted] = useState(new Date());
  const letter = bookParams.letter ?? bookParams.bookCode?.[bookParams.bookCode.length - 1];

  const handleNewRewardModalClose = async () => {
    closeModal();
    handleLeaveWithoutSaving();
  };

  const maybeOpenNewRewardDialog = (result: ReadingLog) => {
    if (result.studentReward) {
      openModal({
        body: <NewSticker character={letter} onClose={handleNewRewardModalClose} />,
        getContainerStyles: (defaultStyles) => ({
          ...defaultStyles,
          maxWidth: '100vw',
          width: '100vw',
        }),
        getPaperStyles: (defaultStyles) => ({
          ...defaultStyles,
          maxWidth: '100vw',
          width: '100vw',
        }),
      });
    } else {
      handleLeaveWithoutSaving();
    }
  };

  const postReadingActivity = useCallback(async () => {
    const pagesReadCount = Object.keys(pagesRead).length;
    if (!book?.id || !pagesReadCount) {
      handleLeaveWithoutSaving();
      return;
    }

    const data = {
      bookId: book?.id,
      pagesRead: pagesReadCount,
      dateStarted: dateStarted,
      dateCompleted: new Date(),
    };
    const result = await createReadingLog({ variables: { data } });
    maybeOpenNewRewardDialog(result.data?.createReadingLog);
  }, [pagesRead, dateStarted, book, nextLocation, handleLeaveWithoutSaving]);

  useEffect(() => {
    blockRouter();
  }, []);

  useEffect(() => {
    activePages.forEach((page) => {
      if (page && typeof page === 'number' && !pagesRead[page]) {
        setPagesRead((state) => ({ ...state, [page]: true }));
      }
    });
  }, [activePages, pagesRead, blockRouter]);

  useEffect(() => {
    if (nextLocation) {
      postReadingActivity();
    }
  }, [nextLocation, postReadingActivity]);
};

export default useReadingLog;
